import React, { useEffect, useState } from 'react';

const SLOW_AFTER_MS = 8000;

/**
 * Full-screen loading state while the app signs in and loads the workspace.
 * Same markup/classes as the splash in public/index.html (styles live there),
 * so the handover from "downloading" to "signing in" has no visual jump.
 *
 * After a few seconds it says the connection is slow; with `error` it stops
 * spinning and offers Retry / Sign out instead of waiting forever.
 */
export default function AppLoadingScreen({
  message = 'Loading Advicelink…',
  error = null,
  onRetry,
  onSignOut,
  retrying = false,
}) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (error) return undefined;
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [error, message]);

  if (error) {
    return (
      <div className="al-splash" role="alert">
        <img src="/logo.png" alt="Advicelink" />
        <div className="al-splash-text" style={{ fontWeight: 600 }}>{error.title}</div>
        <div className="al-splash-text" style={{ maxWidth: 360, textAlign: 'center', opacity: 0.8 }}>{error.detail}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onRetry ? (
            <button type="button" className="al-splash-button al-splash-button-primary" onClick={onRetry} disabled={retrying}>
              {retrying ? 'Retrying…' : 'Retry'}
            </button>
          ) : null}
          {onSignOut ? (
            <button type="button" className="al-splash-button" onClick={onSignOut}>Sign out</button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="al-splash" role="status" aria-live="polite">
      <img src="/logo.png" alt="Advicelink" />
      <div className="al-splash-spinner" aria-hidden="true" />
      <div className="al-splash-text">{message}</div>
      {slow ? (
        <div className="al-splash-text" style={{ maxWidth: 360, textAlign: 'center', opacity: 0.7, fontSize: 13 }}>
          This is taking longer than usual — the connection to the database is slow or dropping. Still trying…
        </div>
      ) : null}
    </div>
  );
}
